import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandCoins, X } from "lucide-react";
import { api, AYLAR, formatTL, extractErrorMessage } from "@/lib/api";
import { AdvancePlanTable, buildPlan, planSum } from "./AdvancePlanTable";
import { StatusPill } from "./AdvanceRequestButton";
import { DateInput } from "@/components/DateInput";

const ApproveModal = ({ req, onClose, onDone }) => {
  const now = new Date();
  const [f, setF] = useState({ toplam_tutar: req.tutar, tarih: now.toISOString().slice(0, 10), taksit_sayisi: 1,
    baslangic_yil: now.getFullYear(), baslangic_ay: now.getMonth() + 1, aciklama: req.aciklama || "" });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (plan && Math.abs(planSum(plan) - Number(f.toplam_tutar)) > 0.01) { toast.error("Taksit planı toplamı tutara eşit olmalı"); return; }
    setBusy(true);
    try {
      await api.post(`/advance-requests/${req.id}/approve`, {
        personnel_id: req.personnel_id, tarih: f.tarih, aciklama: f.aciklama, toplam_tutar: Number(f.toplam_tutar),
        taksit_sayisi: plan ? new Set(plan.map((i) => `${i.yil}-${i.ay}`)).size : Number(f.taksit_sayisi),
        baslangic_yil: plan ? plan[0].yil : Number(f.baslangic_yil), baslangic_ay: plan ? plan[0].ay : Number(f.baslangic_ay),
        ...(plan ? { plan: plan.map((i) => ({ yil: i.yil, ay: i.ay, tutar: Number(i.tutar) || 0, kaynak: i.kaynak })) } : {}),
      });
      toast.success("Talep onaylandı · avans listesine eklendi");
      onDone();
    } catch (e) { toast.error(extractErrorMessage(e, "Onaylanamadı")); } finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="approve-request-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div><h3 className="font-display font-bold">Talebi Onayla · {req.ad_soyad}</h3><div className="text-xs text-slate-500">İstenen {formatTL(req.tutar)}</div></div>
          <button onClick={onClose} data-testid="approve-request-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-semibold text-slate-600">Onaylanan Tutar<input type="number" step="0.01" value={f.toplam_tutar} onChange={set("toplam_tutar")} className={inp} data-testid="approve-tutar" /></label>
            <label className="text-xs font-semibold text-slate-600">Veriliş Tarihi<DateInput value={f.tarih} onChange={set("tarih")} className={inp} data-testid="approve-tarih" /></label>
            <label className="text-xs font-semibold text-slate-600">Taksit<input type="number" min="1" disabled={!!plan} value={f.taksit_sayisi} onChange={set("taksit_sayisi")} className={inp} data-testid="approve-taksit" /></label>
            <label className="text-xs font-semibold text-slate-600">Başlangıç
              <div className="flex gap-1">
                <select disabled={!!plan} value={f.baslangic_ay} onChange={set("baslangic_ay")} className={inp} data-testid="approve-ay">{AYLAR.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}</select>
                <input type="number" disabled={!!plan} value={f.baslangic_yil} onChange={set("baslangic_yil")} className={`${inp} w-24`} data-testid="approve-yil" />
              </div>
            </label>
          </div>
          <div className="flex gap-2 flex-wrap text-[11px]">
            <button type="button" data-testid="approve-plan-open" onClick={() => setPlan(buildPlan(f.toplam_tutar, f.taksit_sayisi, f.baslangic_yil, f.baslangic_ay, false))}
              className="px-2 py-1 border border-slate-300 rounded font-semibold">{plan ? "Yeniden oluştur" : "Ay ay düzenle / primden kes"}</button>
            {plan && <button type="button" data-testid="approve-plan-round" onClick={() => setPlan(buildPlan(f.toplam_tutar, plan.length, plan[0].yil, plan[0].ay, true, plan[0].kaynak))} className="px-2 py-1 border border-slate-300 rounded font-semibold">Tam liraya yuvarla</button>}
            {plan && <button type="button" onClick={() => setPlan(null)} className="text-slate-500 hover:underline">Eşit taksite dön</button>}
          </div>
          {plan && <AdvancePlanTable plan={plan} setPlan={setPlan} toplam={f.toplam_tutar} />}
          <label className="block text-xs font-semibold text-slate-600">Açıklama<input value={f.aciklama} onChange={set("aciklama")} className={inp} /></label>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
          <button onClick={submit} disabled={busy || !(Number(f.toplam_tutar) > 0)} data-testid="approve-request-submit"
            className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold disabled:opacity-50">Onayla ve Avans Aç</button>
        </div>
      </div>
    </div>
  );
};

export const AdvanceRequestsPanel = ({ isAdmin, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [approve, setApprove] = useState(null);
  const load = () => api.get("/advance-requests").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const pending = rows.filter((r) => r.status === "bekliyor");
  const list = showAll ? rows : pending;
  if (!rows.length) return null;
  const reject = async (r) => {
    const neden = window.prompt(`${r.ad_soyad} · ${formatTL(r.tutar)} talebi reddedilsin mi? Neden (isteğe bağlı):`, "");
    if (neden === null) return;
    try { await api.post(`/advance-requests/${r.id}/reject`, { neden }); toast.success("Talep reddedildi"); load(); onChanged?.(); }
    catch (e) { toast.error(extractErrorMessage(e, "Reddedilemedi")); }
  };
  return (
    <div className="mb-4 border border-sky-200 bg-sky-50 rounded-xl p-3" data-testid="advance-requests-panel">
      <div className="flex items-center gap-2 text-sm font-semibold text-sky-900 mb-2">
        <HandCoins size={15} /> <span data-testid="advance-requests-pending">{pending.length} bekleyen avans talebi</span>
        <button type="button" onClick={() => setShowAll(!showAll)} data-testid="advance-requests-toggle" className="ml-auto text-xs font-semibold text-sky-800 hover:underline">{showAll ? "Sadece bekleyenler" : "Tüm talepler"}</button>
      </div>
      <div className="space-y-1.5">
        {list.map((r) => (
          <div key={r.id} className="flex items-center gap-2 bg-white border border-sky-100 rounded-md px-2 py-1.5 text-sm flex-wrap" data-testid={`advance-request-row-${r.id}`}>
            <span className="font-semibold">{r.ad_soyad}</span>
            <span className="font-mono-tab font-bold text-rose-700">{formatTL(r.onaylanan_tutar ?? r.tutar)}</span>
            {r.onaylanan_tutar != null && r.onaylanan_tutar !== r.tutar && <span className="text-[11px] text-slate-400 line-through">{formatTL(r.tutar)}</span>}
            <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString("tr-TR")}</span>
            <span className="flex-1 text-xs text-slate-600 truncate">{r.aciklama}{r.red_nedeni ? ` · Red: ${r.red_nedeni}` : ""}</span>
            <StatusPill s={r.status} />
            {isAdmin && r.status === "bekliyor" && (
              <>
                <button onClick={() => setApprove(r)} data-testid={`advance-request-approve-${r.id}`} className="px-2.5 py-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded text-xs font-semibold">Onayla</button>
                <button onClick={() => reject(r)} data-testid={`advance-request-reject-${r.id}`} className="px-2.5 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 rounded text-xs font-semibold">Reddet</button>
              </>
            )}
          </div>
        ))}
        {!list.length && <div className="text-xs text-slate-500">Bekleyen talep yok.</div>}
      </div>
      {approve && <ApproveModal req={approve} onClose={() => setApprove(null)} onDone={() => { setApprove(null); load(); onChanged?.(); }} />}
    </div>
  );
};
